import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Upload, Trash2, RefreshCw, FileText, ChevronDown, ChevronUp, X, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import * as sopApi from '../../api/sopAdmin';
import api from '../../api/client';
import { adminApi } from '../../api/admin';
import toast from 'react-hot-toast';

export default function SOPManager() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordTypes, setRecordTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Create form
  const [createForm, setCreateForm] = useState({
    title: '', description: '', record_type_id: '', role_id: '', record_type_ids: [],
  });

  const loadEntries = useCallback(async () => {
    try {
      const data = await sopApi.getSOPEntries();
      setEntries(data);
    } catch (err) {
      toast.error('Failed to load SOPs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
    // Load record types and roles for dropdowns
    adminApi.getRecordTypes().then(res => setRecordTypes(Array.isArray(res.data) ? res.data : res.data?.recordTypes || res.data?.record_types || [])).catch(() => {});
    api.get('/roles').then(res => { const d = res.data; setRoles(Array.isArray(d) ? d : d?.roles || d?.data || []); }).catch(() => {});
  }, [loadEntries]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.title.trim()) return;
    try {
      await sopApi.createSOPEntry({
        title: createForm.title.trim(),
        description: createForm.description.trim() || null,
        record_type_id: createForm.record_type_id || null,
        record_type_ids: createForm.record_type_ids || [],
        role_id: createForm.role_id || null,
      });
      toast.success('SOP entry created');
      setCreateForm({ title: '', description: '', record_type_id: '', role_id: '', record_type_ids: [] });
      setShowCreate(false);
      loadEntries();
    } catch (err) {
      toast.error('Failed to create SOP entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this SOP entry and all its files? This cannot be undone.')) return;
    try {
      await sopApi.deleteSOPEntry(id);
      toast.success('SOP entry deleted');
      if (expandedId === id) { setExpandedId(null); setExpandedEntry(null); }
      loadEntries();
    } catch (err) {
      toast.error('Failed to delete SOP entry');
    }
  };

  const handleToggleActive = async (entry) => {
    try {
      await sopApi.updateSOPEntry(entry.id, { is_active: !entry.is_active });
      toast.success(entry.is_active ? 'SOP deactivated' : 'SOP activated');
      loadEntries();
      if (expandedId === entry.id) loadExpandedEntry(entry.id);
    } catch (err) {
      toast.error('Failed to update SOP');
    }
  };

  const loadExpandedEntry = async (id) => {
    try {
      const data = await sopApi.getSOPEntry(id);
      setExpandedEntry(data);
    } catch (err) {
      toast.error('Failed to load SOP details');
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedEntry(null);
    } else {
      setExpandedId(id);
      loadExpandedEntry(id);
    }
  };

  const handleFileUpload = async (sopId, file) => {
    try {
      await sopApi.uploadSOPFile(sopId, file);
      toast.success('File uploaded — processing started');
      loadExpandedEntry(sopId);
      loadEntries();
    } catch (err) {
      toast.error('Failed to upload file');
    }
  };

  const handleFileDelete = async (sopId, fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await sopApi.deleteSOPFile(sopId, fileId);
      toast.success('File deleted');
      loadExpandedEntry(sopId);
      loadEntries();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleReprocess = async (sopId) => {
    try {
      await sopApi.reprocessSOPEntry(sopId);
      toast.success('Reprocessing started');
      setTimeout(() => { loadExpandedEntry(sopId); loadEntries(); }, 3000);
    } catch (err) {
      toast.error('Failed to start reprocessing');
    }
  };

  const handleEditSave = async (id) => {
    try {
      await sopApi.updateSOPEntry(id, {
        title: editForm.title,
        description: editForm.description,
        record_type_id: editForm.record_type_id || null,
        record_type_ids: editForm.record_type_ids || [],
        role_id: editForm.role_id || null,
      });
      toast.success('SOP updated');
      setEditingId(null);
      loadEntries();
      if (expandedId === id) loadExpandedEntry(id);
    } catch (err) {
      toast.error('Failed to update SOP');
    }
  };

  const filtered = entries.filter(e =>
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    (e.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatBytes = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="Knowledge Base / SOPs" description="Upload firm procedures and guidelines. Active SOPs are automatically included in AI chat context." icon={BookOpen} iconColor="text-emerald-600" iconBg="bg-emerald-50">
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-sm font-medium"
        >
          <Plus className="h-4 w-4" /> New SOP
        </button>
      </AdminPageHeader>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Create SOP Entry</h3>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={createForm.title}
                onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="e.g. New Case Intake Procedure"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={createForm.description}
                onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                rows={2}
                placeholder="Brief description of what this SOP covers..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Applies to Record Type</label>
                <select
                  multiple
                  value={createForm.record_type_ids || []}
                  onChange={e => setCreateForm({ ...createForm, record_type_ids: Array.from(e.target.selectedOptions, o => o.value), record_type_id: Array.from(e.target.selectedOptions, o => o.value)[0] || null })}
                  size={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">All Record Types</option>
                  {recordTypes.map(rt => (
                    <option key={rt.id} value={rt.id}>{rt.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Applies to Role</label>
                <select
                  value={createForm.role_id}
                  onChange={e => setCreateForm({ ...createForm, role_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">All Roles</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                Create SOP
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          placeholder="Search SOPs..."
        />
      </div>

      {/* SOP List */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          {entries.length === 0 ? 'No SOPs created yet. Click "New SOP" to get started.' : 'No SOPs match your search.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(entry => (
            <div key={entry.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Entry Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition"
                onClick={() => toggleExpand(entry.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    entry.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    {editingId === entry.id ? (
                      <input
                        value={editForm.title}
                        onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                        onClick={e => e.stopPropagation()}
                        className="border border-gray-300 rounded px-2 py-1 text-sm font-semibold"
                      />
                    ) : (
                      <h3 className="font-semibold text-sm text-gray-900 truncate">{entry.title}</h3>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      {(entry.record_type_ids?.length > 0 || entry.record_type_name) && (
                        <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                          {entry.record_type_ids?.length > 1 ? `${entry.record_type_ids.length} types` : entry.record_type_name || 'All Types'}
                        </span>
                      )}
                      {entry.role_name && (
                        <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{entry.role_name}</span>
                      )}
                      <span>{entry.file_count || 0} file{entry.file_count !== 1 ? 's' : ''}</span>
                      <span>{entry.chunk_count || 0} chunks</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    entry.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {entry.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {expandedId === entry.id
                    ? <ChevronUp className="h-4 w-4 text-gray-400" />
                    : <ChevronDown className="h-4 w-4 text-gray-400" />
                  }
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedId === entry.id && expandedEntry && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                  {/* Description & Edit */}
                  {editingId === entry.id ? (
                    <div className="space-y-3 mb-4">
                      <textarea
                        value={editForm.description}
                        onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        rows={2}
                        placeholder="Description..."
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          multiple
                          value={editForm.record_type_ids || (editForm.record_type_id ? [editForm.record_type_id] : [])}
                          onChange={e => setEditForm({ ...editForm, record_type_ids: Array.from(e.target.selectedOptions, o => o.value), record_type_id: Array.from(e.target.selectedOptions, o => o.value)[0] || null })}
                          size={4}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">All Record Types</option>
                          {recordTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                        </select>
                        <select
                          value={editForm.role_id}
                          onChange={e => setEditForm({ ...editForm, role_id: e.target.value })}
                          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">All Roles</option>
                          {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleEditSave(entry.id)} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium">Save</button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-gray-600 text-xs">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {expandedEntry.description && (
                        <p className="text-sm text-gray-600 mb-3">{expandedEntry.description}</p>
                      )}
                    </>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 mb-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleActive(entry); }}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                        entry.is_active
                          ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      }`}
                    >
                      {entry.is_active ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                      {entry.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(entry.id);
                        setEditForm({
                          title: expandedEntry.title,
                          description: expandedEntry.description || '',
                          record_type_id: expandedEntry.record_type_id || '',
              record_type_ids: expandedEntry.record_type_ids || (expandedEntry.record_type_id ? [expandedEntry.record_type_id] : []),
                          role_id: expandedEntry.role_id || '',
                        });
                      }}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleReprocess(entry.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100"
                    >
                      <RefreshCw className="h-3 w-3" /> Reprocess
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 ml-auto"
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>

                  {/* Files */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Files</h4>
                      <label className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 cursor-pointer">
                        <Upload className="h-3 w-3" /> Upload File
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.txt,.csv,.md,.html"
                          onChange={e => {
                            if (e.target.files[0]) {
                              handleFileUpload(entry.id, e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                        />
                      </label>
                    </div>

                    {(!expandedEntry.files || expandedEntry.files.length === 0) ? (
                      <p className="text-xs text-gray-500 py-4 text-center">No files uploaded yet</p>
                    ) : (
                      <div className="space-y-1">
                        {expandedEntry.files.map(file => (
                          <div key={file.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm text-gray-900 truncate">{file.original_name}</p>
                                <p className="text-xs text-gray-500">
                                  {formatBytes(file.size_bytes)}
                                  {file.is_processed ? (
                                    file.processing_error
                                      ? <span className="ml-2 text-red-500">Error: {file.processing_error}</span>
                                      : <span className="ml-2 text-emerald-600">Processed</span>
                                  ) : (
                                    <span className="ml-2 text-amber-600">Processing...</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleFileDelete(entry.id, file.id)}
                              className="text-gray-400 hover:text-red-500 transition p-1"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
